// Pricing contracts (docs/use-cases/pricing.md, UC-PRICING-001..012; modules-and-api.md §5.11; working decisions W1–W3, W7,
// W8 and W13 of docs/sprints/sprint-04.md).
//
// Sprint 4 foundation: the vocabularies the pricing tables (their CHECK constraints) and routes share, spelled as the use
// cases spell them. Money is integer cents and percentages integer basis points (BR-PRICING-001): selling prices VAT-inclusive,
// costs ex VAT (W1); only `@visionx/money` multiplies money (`markup`, `roundPrice`, `addVat`, `netOfDiscount`,
// `timesQuantity`). The rounding rules are `ROUNDING_RULES` (tenancy.ts), beside the `pricing` settings that choose them
// (BR-PRICING-018); the markup and discount ranges are `MarkupBpSchema` and `DiscountBpSchema` (primitives.ts).
//
// The pricing module's build (Sprint 4 stage 3) adds its request and response schemas at the end of this file. A body field
// answers the codes the use cases name — `required`, `invalid` (a float, a string or a fraction of a cent, BR-PRICING-001),
// `out_of_range` — through the small field builders below rather than Zod's own codes, so every money and basis-point field
// is refused the same way, naming the field (TC-PRICING-076).
import { z } from "zod";
import { PRODUCT_CATEGORIES, VAT_TREATMENTS, type ProductCategory } from "./catalogue";
import { DISCOUNT_BP_MAX, LINE_QUANTITY_MAX, MARKUP_BP_MAX, UNIT_COST_MAX_CENTS } from "./primitives";
import { ROUNDING_RULES } from "./tenancy";

/**
 * BR-PRICING-010: where the waterfall found a line's unit price — the serial's own price, the branch price in force, the
 * tenant price in force, or none (R 0.00 with `requiresManualEntry`). A document line snapshots it (BR-PRICING-025).
 */
export const PRICE_SOURCES = ["SerialPrice", "BranchPrice", "TenantPrice", "None"] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];
/** BR-PRICING-012 (W13): the source of a part a lens component option adds to a lens line's price. */
export const OPTION_PRICE_SOURCE = "OptionPrice" as const;

/**
 * BR-PRICING-013: what started a price-history entry. `Copy` (medical-aid tariffs, Sprint 5) and `BillVariance` (supplier
 * bills, Sprint 7) join with their features.
 */
export const PRICE_HISTORY_SOURCES = ["Manual", "Receipt", "OpeningStock", "Import"] as const;
export type PriceHistorySource = (typeof PRICE_HISTORY_SOURCES)[number];

/**
 * BR-PRICING-013: what a price-history entry changed — a tenant price (the product's), a branch price, a serial's own price, a
 * band, a product markup, a price list's terms, a lab cost or a component option's price or cost. The use cases spell
 * `FrameMarkupBand` and `SupplierPriceList` (UC-PRICING-003 A4, `?entity=`); the others follow the design's
 * `price_history.entity` (schema-catalogue.sql), less `MedicalAidPricing` (Sprint 5) and `SupplierComponentCode` (not built, W13).
 */
export const PRICE_HISTORY_ENTITIES = [
  "Product", "BranchPrice", "Serial", "FrameMarkupBand", "ProductMarkup", "SupplierPriceList", "SupplierLensCode", "LensComponentOption",
] as const;
export type PriceHistoryEntity = (typeof PRICE_HISTORY_ENTITIES)[number];
/** What an entry's old and new values are: cents, basis points, a count or a text (a date). */
export const PRICE_HISTORY_VALUE_KINDS = ["Money", "Percent", "Number", "Text"] as const;
export type PriceHistoryValueKind = (typeof PRICE_HISTORY_VALUE_KINDS)[number];

/**
 * BR-PRICING-005: which band prices a frame — each matching key scores product 8 + supplier 4 + brand 2 + branch 1, and the
 * highest wins (R-06). The no-overlap rule (BR-PRICING-004) makes a tie impossible.
 */
export const MARKUP_BAND_SCORES = { product: 8, supplier: 4, brand: 2, branch: 1 } as const;

/**
 * BR-PRICING-019 (the cross-module review of 16 Oct 2026): a supplier price list's kind in Sprint 4 — a lens lab's cost list.
 * The design's `Fitting`, `Frame` and `ContactLens` kinds are not built: a lab's fitting-fee list waits with the fitting-fee
 * rules, frame and contact-lens cost lists with the product import (Sprint 11).
 */
export const SUPPLIER_PRICE_LIST_KINDS = ["Lens", "Coating", "Colour", "PPF", "Mixed"] as const;
export type SupplierPriceListKind = (typeof SUPPLIER_PRICE_LIST_KINDS)[number];

/**
 * BR-PRICING-019, BR-PRICING-023 (W8): an import creates a list as a Draft; the import's final guarded write makes it Current
 * and the supplier's previous Current list of the same scope Superseded. A Draft whose import is discarded after its run
 * failed is withdrawn — never Current, its lines kept (BR-IMPORTS-020).
 */
export const SUPPLIER_PRICE_LIST_STATUSES = ["Draft", "Current", "Superseded"] as const;
export type SupplierPriceListStatus = (typeof SUPPLIER_PRICE_LIST_STATUSES)[number];

/** BR-PRICING-015: the warnings a markup proposal answers in place of a figure — no band or markup matches; no list in force. */
export const PROPOSAL_WARNINGS = ["no_markup", "no_list"] as const;
export type ProposalWarning = (typeof PROPOSAL_WARNINGS)[number];

// ===========================================================================================================================
// The pricing module's request and response schemas (UC-PRICING-001..012; modules-and-api.md §5.11)
// ===========================================================================================================================

/** UC-PRICING-008 E5: a waterfall request prices 1 to 100 lines (Proposed: D1's 1 000 queries per invocation). */
export const RESOLVE_LINES_MAX = 100;
/** UC-PRICING-012, BR-PRICING-027: a price-list export holds at most 50 000 rows (the house export cap, BR-GOVERNANCE-015). */
export const PRICE_LIST_EXPORT_ROW_CAP = 50_000;
/** BR-PRICING-013: the price history's page — 50 by default, at most 200 (UC-PRICING-003 E2). */
export const PRICE_HISTORY_PAGE = { limitDefault: 50, limitMax: 200 } as const;
/** BR-PRICING-004: a band keyed to a brand names it as a product does (BR-CATALOGUE-005's 60 characters). */
export const BAND_BRAND_MAX = 60;
/** UC-PRICING-002: a branch price's reason, written to its Control Log entry and history entry — at most 500 characters, as every reason. */
export const BRANCH_PRICE_REASON_MAX = 500;
/** The most component options one lens line names (Proposed: a lens takes a coating, a tint and a PPF filter at most twice over). */
export const RESOLVE_OPTIONS_MAX = 6;

// --- Field builders: each answers the use cases' codes (`required`, `invalid`, `out_of_range`) -----------------------------

type Ctx = z.RefinementCtx;
const fail = (ctx: Ctx, code: string, message: string): void => { ctx.addIssue({ code: "custom", message, params: { code } }); };
const absent = (v: unknown): boolean => v === undefined || v === null;

/** Whether `s` is a calendar day `YYYY-MM-DD` that exists (`2026-02-30`, `2026-13-01` and `10/09/2026` do not). */
export function isCalendarDate(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d <= days;
}

interface WholeRule { min: number; max: number; required: boolean; nullable?: boolean | undefined; what: string }

/**
 * A whole number held to its range (BR-PRICING-001): a fraction, a float, a string or anything not a number is `invalid`
 * ("a float, a string or a fraction of a cent is refused"), a value outside the range `out_of_range`, a missing one `required`.
 * `null` is a value only where the field may be cleared (a branch price's removal, an open band end).
 */
const wholeSchema = (r: WholeRule) => z.unknown().superRefine((v, ctx) => {
  if (v === undefined) { if (r.required) fail(ctx, "required", "required"); return; }
  if (v === null) { if (!r.nullable) fail(ctx, r.required ? "required" : "invalid", r.required ? "required" : `${r.what}, not null`); return; }
  if (typeof v !== "number" || !Number.isSafeInteger(v)) return fail(ctx, "invalid", `${r.what} as a whole number`);
  if (v < r.min || v > r.max) fail(ctx, "out_of_range", `${r.what} from ${r.min} to ${r.max}`);
});
/** Money in whole cents (BR-PRICING-001), at least 0. */
const centsRule = (o: { required: boolean; nullable?: boolean | undefined; max?: number | undefined }): WholeRule => ({ min: 0, max: o.max ?? Number.MAX_SAFE_INTEGER, required: o.required, nullable: o.nullable, what: "whole cents of at least 0" });
const requiredCents = (max?: number) => wholeSchema(centsRule({ required: true, max })).transform((v) => v as number);
const optionalCents = (max?: number) => wholeSchema(centsRule({ required: false, max })).transform((v) => v as number | undefined).optional();
const nullableCents = (o: { required: boolean }) => wholeSchema(centsRule({ required: o.required, nullable: true })).transform((v) => v as number | null);
/** A markup percentage in whole basis points, 0–100 000 (0–1 000 %, BR-PRICING-004, -007; the cap is Proposed). */
const markupBp = (required: boolean) => wholeSchema({ min: 0, max: MARKUP_BP_MAX, required, what: "whole basis points" });
const requiredMarkupBp = () => markupBp(true).transform((v) => v as number);
const optionalMarkupBp = () => markupBp(false).transform((v) => v as number | undefined).optional();

const idSchema = (required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (absent(v)) { if (required) fail(ctx, "required", "required"); return; }
  if (typeof v !== "string" || v.trim().length === 0 || v.length > 64) fail(ctx, "invalid", "an id");
});
/** An id of a record of the organisation (whether it holds one is the service's 404), required. */
const requiredId = () => idSchema(true).transform((v) => (v as string).trim());
/** An id when given (`null` is none). */
const optionalId = () => idSchema(false).transform((v) => (absent(v) ? undefined : (v as string).trim())).optional();
/** An id when given; `null` is "none" (a tenant scope, no key). */
const nullableId = () => idSchema(false).transform((v) => (v === undefined ? undefined : v === null ? null : (v as string).trim())).optional();

/** A calendar day `YYYY-MM-DD` in the organisation's timezone (BR-PRICING-014). */
const dateSchema = (required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (absent(v)) { if (required) fail(ctx, "required", "required"); return; }
  if (!isCalendarDate(v)) fail(ctx, "invalid", "a calendar date YYYY-MM-DD");
});
const requiredDate = () => dateSchema(true).transform((v) => v as string);
const optionalDate = () => dateSchema(false).transform((v) => (absent(v) ? undefined : (v as string))).optional();

const optionalText = (max: number) => z.unknown().superRefine((v, ctx) => {
  if (absent(v)) return;
  if (typeof v !== "string") return fail(ctx, "invalid", "text");
  if (v.trim().length > max) fail(ctx, "too_long", `at most ${max} characters`);
}).transform((v): string | null | undefined => (v === undefined ? undefined : v === null || (v as string).trim() === "" ? null : (v as string).trim())).optional();

const queryFlag = z.enum(["true", "false"]).optional();
const limitParam = (max: number, def: number) => z.unknown().superRefine((v, ctx) => {
  const n = typeof v === "string" && /^\d+$/.test(v) ? Number(v) : NaN;
  if (!Number.isInteger(n) || n < 1 || n > max) fail(ctx, "out_of_range", `1 to ${max}`);
}).transform((v): number => Number(v)).default(def);
const cursorParam = z.string().max(512).optional();
/** A query's id: any text up to 64 characters, so a malformed id is not found, like a foreign one. */
const queryId = z.string().min(1).max(64).optional();
/** `org` for the tenant's records, or a branch id (UC-PRICING-004, -005, -009 lists). */
const scopeParam = z.string().min(1).max(64).optional();

// --- Shared views -----------------------------------------------------------------------------------------------------------

export const PricingUserRefSchema = z.object({ id: z.string(), controlId: z.number().int().nullable(), displayName: z.string() });
export type PricingUserRef = z.infer<typeof PricingUserRefSchema>;
export const PricingBranchRefSchema = z.object({ id: z.string(), code: z.string(), name: z.string() });
export type PricingBranchRef = z.infer<typeof PricingBranchRefSchema>;
export const PricingProductRefSchema = z.object({ id: z.string(), sku: z.string(), name: z.string(), category: z.enum(PRODUCT_CATEGORIES) });
export type PricingProductRef = z.infer<typeof PricingProductRefSchema>;
export const PricingSupplierRefSchema = z.object({ id: z.string(), name: z.string(), active: z.boolean() });
export type PricingSupplierRef = z.infer<typeof PricingSupplierRefSchema>;
/** Where a band, a markup or a list applies: the tenant (every branch) or one branch (its override). */
export const PRICING_SCOPES = ["Tenant", "Branch"] as const;
export type PricingScope = (typeof PRICING_SCOPES)[number];

// --- The waterfall (UC-PRICING-008; BR-PRICING-010..012, BR-PRICING-014, BR-PRICING-025) ------------------------------------

export const ResolveLineInputSchema = z.strictObject({
  productId: requiredId(),
  /** A serial of the line's product held at the branch (BR-PRICING-011). */
  serialId: optionalId(),
  /** Whole units, 1–9 999 (W4). */
  quantity: wholeSchema({ min: 1, max: LINE_QUANTITY_MAX, required: true, what: "whole units" }).transform((v) => v as number),
  /** The lens's own supplier's component options chosen with it (W13, BR-PRICING-012). */
  componentOptionIds: z.array(z.string().min(1).max(64)).max(RESOLVE_OPTIONS_MAX).optional(),
});
export type ResolveLineInput = z.infer<typeof ResolveLineInputSchema>;

export const ResolveRequestSchema = z.strictObject({
  branchId: requiredId(),
  /** Named by a quote or order (Sprint 5); no Sprint 4 leg reads it — nothing defines a per-patient price (UC-PRICING-008 open questions). */
  patientId: optionalId(),
  documentDate: requiredDate(),
  lines: z.array(ResolveLineInputSchema).min(1).max(RESOLVE_LINES_MAX),
});
export type ResolveRequest = z.infer<typeof ResolveRequestSchema>;

/** A part of a line's price: the product's own price, then each component option's (BR-PRICING-012). */
export const ResolvedPartSchema = z.object({
  source: z.enum([...PRICE_SOURCES, OPTION_PRICE_SOURCE]),
  productId: z.string().nullable(),
  optionId: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string(),
  unitPriceCents: z.number().int(),
  sourceId: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
});
export type ResolvedPart = z.infer<typeof ResolvedPartSchema>;

export const ResolvedLineSchema = z.object({
  lineNo: z.number().int(),
  productId: z.string(),
  serialId: z.string().nullable(),
  quantity: z.number().int(),
  /** VAT-inclusive cents (W1): the product's price and its options'. */
  unitPriceCents: z.number().int(),
  /** unit price × quantity (`timesQuantity`, TC-MONEY-020). */
  lineTotalCents: z.number().int(),
  priceSource: z.enum(PRICE_SOURCES),
  /** The record the price came from: the serial, the branch price row, or the tenant price's history entry. */
  sourceId: z.string().nullable(),
  /** The day the price took effect: a branch price's start, the tenant price's last change. */
  effectiveFrom: z.string().nullable(),
  vatTreatment: z.enum(VAT_TREATMENTS),
  /** The organisation's rate for Standard (1 500 bp), 0 for zero-rated and exempt. */
  vatRateBp: z.number().int(),
  requiresManualEntry: z.boolean(),
  parts: z.array(ResolvedPartSchema),
});
export type ResolvedLine = z.infer<typeof ResolvedLineSchema>;
export const ResolveResultSchema = z.object({ branchId: z.string(), documentDate: z.string(), lines: z.array(ResolvedLineSchema) });
export type ResolveResult = z.infer<typeof ResolveResultSchema>;

// --- The markup proposal (UC-PRICING-007; BR-PRICING-002, -003, -005..007, -015, -021) --------------------------------------

export const SuggestRequestSchema = z.strictObject({
  branchId: requiredId(),
  /** A frame, a contact lens, an accessory or a stocked lens (BR-PRICING-006). */
  productId: optionalId(),
  /** A lens lab, for a lens component option being entered (A9). */
  supplierId: optionalId(),
  /** The cost ex VAT (W1), whole cents R 0.00–R 999 999.99. */
  netCostCents: requiredCents(UNIT_COST_MAX_CENTS),
}).superRefine((v, ctx) => {
  if (v.productId === undefined && v.supplierId === undefined) ctx.addIssue({ code: "custom", path: ["productId"], message: "name a product, or a lens lab for an option", params: { code: "required" } });
  else if (v.productId !== undefined && v.supplierId !== undefined) ctx.addIssue({ code: "custom", path: ["productId"], message: "name a product or a supplier, not both", params: { code: "ambiguous" } });
});
export type SuggestRequest = z.infer<typeof SuggestRequestSchema>;

export const FrameMarkupBandRefSchema = z.object({
  id: z.string(), scope: z.enum(PRICING_SCOPES), branchId: z.string().nullable(), productId: z.string().nullable(), supplierId: z.string().nullable(), brand: z.string().nullable(),
  rangeStartCents: z.number().int(), rangeEndCents: z.number().int().nullable(), marginPercentBp: z.number().int(), marginValueCents: z.number().int(), specificityScore: z.number().int(),
});
export type FrameMarkupBandRef = z.infer<typeof FrameMarkupBandRefSchema>;
export const ProductMarkupRefSchema = z.object({ id: z.string(), scope: z.enum(PRICING_SCOPES), branchId: z.string().nullable(), percentBp: z.number().int(), valueCents: z.number().int() });
export type ProductMarkupRef = z.infer<typeof ProductMarkupRefSchema>;
export const SupplierPriceListRefSchema = z.object({
  id: z.string(), name: z.string(), scope: z.enum(PRICING_SCOPES), branchId: z.string().nullable(), listDate: z.string(), lensMarkupBp: z.number().int().nullable(),
});
export type SupplierPriceListRef = z.infer<typeof SupplierPriceListRefSchema>;

export const SUGGESTION_BASES = ["FrameMarkupBand", "ProductMarkup", "SupplierPriceList"] as const;
export type SuggestionBasis = (typeof SUGGESTION_BASES)[number];

export const PriceProposalSchema = z.object({
  /** The VAT-inclusive proposal (W2), rounded (BR-PRICING-003). */
  suggestedIncVatCents: z.number().int(),
  /** Its ex-VAT part (`exVat`), for a document that needs it (BR-PRICING-001). */
  suggestedExVatCents: z.number().int(),
  basis: z.enum(SUGGESTION_BASES),
  percentBp: z.number().int(),
  valueCents: z.number().int(),
  roundingRule: z.enum(ROUNDING_RULES),
  band: FrameMarkupBandRefSchema.nullable(),
  productMarkup: ProductMarkupRefSchema.nullable(),
  priceList: SupplierPriceListRefSchema.nullable(),
  lensMarkupBp: z.number().int().nullable(),
});
export type PriceProposal = z.infer<typeof PriceProposalSchema>;

export const SuggestResultSchema = z.object({
  branchId: z.string(),
  productId: z.string().nullable(),
  supplierId: z.string().nullable(),
  netCostCents: z.number().int(),
  /** Whether the branch proposes prices at receipt (`proposePriceAtReceipt`, BR-PRICING-018). */
  proposePriceAtReceipt: z.boolean(),
  /** The rule the branch's setting gives (`roundingRule`, or `lensRoundingRule` for a supplier's lens rule). */
  roundingRule: z.enum(ROUNDING_RULES),
  proposal: PriceProposalSchema.nullable(),
  warning: z.enum(PROPOSAL_WARNINGS).nullable(),
});
export type SuggestResult = z.infer<typeof SuggestResultSchema>;

// --- The price history and the price on a date (UC-PRICING-003; BR-PRICING-013, BR-PRICING-014) ----------------------------

export const PriceHistoryQuerySchema = z.object({
  productId: queryId,
  entity: z.enum(PRICE_HISTORY_ENTITIES).optional(),
  entityId: queryId,
  branchId: queryId,
  source: z.enum(PRICE_HISTORY_SOURCES).optional(),
  /**
   * One field's changes alone, as each entry names what changed (UC-PRICING-003 A3 step 3): `priceCents` a product's tenant price (and
   * a branch price's), `supplierPriceCents` its supplier price, … — so Branch prices reads the tenant price's own last change, never a
   * newer supplier price's (UC-PRICING-002 step 1; the review of change 9, 1 Nov 2026, C9V-CONTRACT-1). A field no entry names lists none.
   */
  field: z.string().min(1).max(64).optional(),
  /** Changed on or after this calendar day (the organisation's timezone). */
  from: optionalDate(),
  /** Changed on or before this calendar day. */
  to: optionalDate(),
  limit: limitParam(PRICE_HISTORY_PAGE.limitMax, PRICE_HISTORY_PAGE.limitDefault),
  cursor: cursorParam,
  count: queryFlag,
}).superRefine((v, ctx) => {
  if (v.entity !== undefined && v.entityId === undefined) ctx.addIssue({ code: "custom", path: ["entityId"], message: "an entity's history names its id", params: { code: "required" } });
  if (v.entityId !== undefined && v.entity === undefined) ctx.addIssue({ code: "custom", path: ["entity"], message: "name the entity the id belongs to", params: { code: "required" } });
});
export type PriceHistoryQuery = z.infer<typeof PriceHistoryQuerySchema>;

export const PriceHistoryEntryViewSchema = z.object({
  id: z.string(),
  entity: z.enum(PRICE_HISTORY_ENTITIES),
  entityId: z.string(),
  productId: z.string().nullable(),
  product: z.object({ id: z.string(), sku: z.string(), name: z.string() }).nullable(),
  branchId: z.string().nullable(),
  branch: PricingBranchRefSchema.nullable(),
  /** What changed: the field of the record (`priceCents`, `marginPercentBp`, `costCents`, `lensMarkupBp` …). */
  field: z.string(),
  valueKind: z.enum(PRICE_HISTORY_VALUE_KINDS),
  /** Cents or basis points; null where there was none. */
  oldValue: z.number().int().nullable(),
  newValue: z.number().int().nullable(),
  oldText: z.string().nullable(),
  newText: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  source: z.enum(PRICE_HISTORY_SOURCES),
  importJobId: z.string().nullable(),
  controlLogId: z.string().nullable(),
  reference: z.object({ type: z.string(), id: z.string(), number: z.string().nullable() }).nullable(),
  /** A receipt's proposal beside the kept price (BR-PRICING-016). */
  proposalCents: z.number().int().nullable(),
  reason: z.string().nullable(),
  changedBy: PricingUserRefSchema.nullable(),
  changedAt: z.string(),
});
export type PriceHistoryEntryView = z.infer<typeof PriceHistoryEntryViewSchema>;
export const PriceHistoryPageSchema = z.object({ items: z.array(PriceHistoryEntryViewSchema), nextCursor: z.string().optional(), totalCount: z.number().int().optional() });

export const PriceAsAtQuerySchema = z.object({ productId: z.string().min(1).max(64), branchId: z.string().min(1).max(64), date: requiredDate() });
export type PriceAsAtQuery = z.infer<typeof PriceAsAtQuerySchema>;
export const PRICE_AS_AT_SOURCES = ["BranchPrice", "TenantPrice", "None"] as const satisfies readonly PriceSource[];
export const PriceAsAtResultSchema = z.object({
  productId: z.string(),
  branchId: z.string(),
  date: z.string(),
  /** VAT-inclusive cents; null for no price. */
  priceCents: z.number().int().nullable(),
  source: z.enum(PRICE_AS_AT_SOURCES),
  /** The branch price row, or the tenant price's history entry. */
  sourceId: z.string().nullable(),
  /** In force since this day. */
  effectiveFrom: z.string().nullable(),
  /** The history entry the price came from. */
  historyEntryId: z.string().nullable(),
  setBy: PricingUserRefSchema.nullable(),
});
export type PriceAsAtResult = z.infer<typeof PriceAsAtResultSchema>;

// --- The cost lookup (UC-PRICING-011; BR-PRICING-020) ------------------------------------------------------------------------

/**
 * Where a cost comes from (BR-PRICING-020): the branch's own list in force, else the organisation's, else — asked by product — the
 * product's supplier price (BR-CATALOGUE-026; DVN's answers of 28 Oct 2026, change 9).
 */
export const COST_SOURCES = ["BranchList", "TenantList", "SupplierPrice"] as const;
export type CostSource = (typeof COST_SOURCES)[number];

export const CostQuerySchema = z.object({
  productId: queryId,
  supplierId: queryId,
  /** The supplier's code, compared ignoring case (BR-IMPORTS-007). */
  code: z.string().trim().min(1).max(40).optional(),
  branchId: z.string().min(1).max(64),
  /** The day the cost is for; today when left out. */
  date: optionalDate(),
}).superRefine((v, ctx) => {
  if (v.productId === undefined && (v.supplierId === undefined || v.code === undefined)) ctx.addIssue({ code: "custom", path: ["productId"], message: "name a product, or a supplier and its code", params: { code: "required" } });
  if (v.productId !== undefined && (v.supplierId !== undefined || v.code !== undefined)) ctx.addIssue({ code: "custom", path: ["productId"], message: "name a product or a supplier and code, not both", params: { code: "ambiguous" } });
});
export type CostQuery = z.infer<typeof CostQuerySchema>;

export const CostResultSchema = z.object({
  productId: z.string().nullable(),
  supplierId: z.string().nullable(),
  code: z.string().nullable(),
  branchId: z.string(),
  date: z.string(),
  /** Nothing gives a cost (A4): no list in force carries the code and the product holds no supplier price — no amount, no list, no source. */
  priceMissing: z.boolean(),
  /** The list price ex VAT — for `SupplierPrice`, the product's supplier price, what the supplier charges before any discount. */
  listCents: z.number().int().nullable(),
  /** The list's own discount; null is the supplier's default (BR-PRICING-019) — always null for `SupplierPrice`, which has no list. */
  discountBp: z.number().int().nullable(),
  /**
   * The discount the net was worked with: a list's line's, frozen on the line (BR-PRICING-020); for `SupplierPrice` the supplier's
   * default as it is read now, never frozen, 0 for a product with no supplier (BR-PURCHASING-007; UC-PRICING-011 A3).
   */
  appliedDiscountBp: z.number().int().nullable(),
  /** The net cost ex VAT (`netOfDiscount`, TC-MONEY-021). */
  netCents: z.number().int().nullable(),
  source: z.enum(COST_SOURCES).nullable(),
  priceListId: z.string().nullable(),
  priceListName: z.string().nullable(),
  listDate: z.string().nullable(),
});
export type CostResult = z.infer<typeof CostResultSchema>;

// --- Branch prices (UC-PRICING-002; BR-PRICING-009, -013, -014, -026) --------------------------------------------------------

export const BranchPriceQuerySchema = z.object({
  productId: queryId,
  branchId: queryId,
  includeVoided: queryFlag,
  limit: limitParam(200, 50),
  cursor: cursorParam,
});
export type BranchPriceQuery = z.infer<typeof BranchPriceQuerySchema>;

export const BranchPricePutSchema = z.strictObject({
  productId: requiredId(),
  branchId: requiredId(),
  /** VAT-inclusive cents of at least R 0.00; `null` removes the branch's price from the date (A3). */
  priceCents: nullableCents({ required: true }),
  /** Today or later in the organisation's timezone (E1). */
  effectiveFrom: requiredDate(),
  /**
   * Why, written to the Control Log entry of the Control PIN every change needs (UC-PRICING-002 steps 3–5; DVN's answer of 24 Oct
   * 2026 to Sprint 4's question 3) and to the price's history entry; at most 500 characters. Required whenever the change needs its
   * PIN — refused `422` (`reason`, `required`) before it is asked (E9; DVN's answer of 28 Oct 2026 to question 52 (b), change 11) —
   * and optional where it needs none: a request that changes nothing (A6), a change off the PIN-protected list (A7).
   */
  reason: optionalText(BRANCH_PRICE_REASON_MAX),
});
export type BranchPricePut = z.infer<typeof BranchPricePutSchema>;

/**
 * `DELETE /pricing/branch-prices/{id}` (UC-PRICING-002 A4; DVN's answer of 28 Oct 2026 to Sprint 4's question 52 (b), change 11): why a
 * branch price not yet in force is cancelled, written to the cancellation's Control Log entry and history entry; at most 500 characters,
 * and nothing else in the body. Required whenever the cancellation needs its Control PIN, refused `422` (`reason`, `required`) before it
 * is asked (E9); optional off the PIN-protected list (A7), where a body may be left out — sent with no media type: an empty body sent
 * as JSON is not JSON, `422` (`body`, `malformed_json`). A body not sent as JSON is `422` (`body`, `unsupported_media_type`) before
 * any PIN (A4 step 3; the verification of 30 Oct 2026, C11-V2).
 */
export const BranchPriceCancelSchema = z.strictObject({ reason: optionalText(BRANCH_PRICE_REASON_MAX) });
export type BranchPriceCancel = z.infer<typeof BranchPriceCancelSchema>;

export const BranchPriceViewSchema = z.object({
  id: z.string(),
  productId: z.string(),
  product: z.object({ id: z.string(), sku: z.string(), name: z.string() }).nullable(),
  branchId: z.string(),
  branch: PricingBranchRefSchema.nullable(),
  /** VAT-inclusive cents; null on a removal row ("no branch price from this date"). */
  priceCents: z.number().int().nullable(),
  isRemoval: z.boolean(),
  effectiveFrom: z.string(),
  priceSource: z.enum(PRICE_HISTORY_SOURCES),
  sourceImportJobId: z.string().nullable(),
  /** The row the branch charges by today (a removal in force says the branch charges the tenant price). */
  inForce: z.boolean(),
  createdBy: PricingUserRefSchema.nullable(),
  createdAt: z.string(),
  voidedAt: z.string().nullable(),
  voidedBy: PricingUserRefSchema.nullable(),
  voidReason: z.string().nullable(),
});
export type BranchPriceView = z.infer<typeof BranchPriceViewSchema>;
export const BranchPricePageSchema = z.object({ items: z.array(BranchPriceViewSchema), nextCursor: z.string().optional() });
export const BranchPriceWriteResultSchema = z.object({
  /**
   * Whether anything was written: the branch's own price already in force from that date, saved again, writes nothing
   * (BR-PRICING-013; UC-PRICING-002 A6) — the tenant price saved where the branch has no price of its own is written (A8).
   */
  changed: z.boolean(),
  /** The row written. */
  price: BranchPriceViewSchema.nullable(),
  /** The branch's live rows for the product, newest start first. */
  rows: z.array(BranchPriceViewSchema),
});
export type BranchPriceWriteResult = z.infer<typeof BranchPriceWriteResultSchema>;

// --- Frame markup bands (UC-PRICING-004; BR-PRICING-004, -005, -013, -026) ---------------------------------------------------

export const FrameMarkupBandQuerySchema = z.object({
  /** `org` for the tenant's bands, a branch id for that branch's overrides; every band when left out. */
  branchId: scopeParam,
  includeRetired: queryFlag,
  limit: limitParam(200, 200),
  cursor: cursorParam,
});
export type FrameMarkupBandQuery = z.infer<typeof FrameMarkupBandQuerySchema>;

const bandRangeRefine = (v: { rangeStartCents?: number | undefined; rangeEndCents?: number | null | undefined }, ctx: Ctx) => {
  if (typeof v.rangeStartCents === "number" && typeof v.rangeEndCents === "number" && v.rangeEndCents <= v.rangeStartCents) {
    ctx.addIssue({ code: "custom", path: ["rangeEndCents"], message: "must be more than the band's from-value", params: { code: "not_above_start" } });
  }
};

export const FrameMarkupBandCreateSchema = z.strictObject({
  /** Null or left out: the tenant's band; a branch id: that branch's override. */
  branchId: nullableId(),
  /** A frame (`not_a_frame`). */
  productId: nullableId(),
  /** An active supplier (`inactive`, LAB-08). */
  supplierId: nullableId(),
  /** Matched to a product's brand ignoring case. */
  brand: optionalText(BAND_BRAND_MAX),
  /** The cost range ex VAT: from inclusive, at least R 0.00. */
  rangeStartCents: requiredCents(),
  /** To exclusive, more than from; null or left out for "and above". */
  rangeEndCents: wholeSchema(centsRule({ required: false, nullable: true })).transform((v) => v as number | null | undefined).optional(),
  marginPercentBp: requiredMarkupBp(),
  /** Added in VAT-inclusive cents (Proposed, BR-PRICING-002); 0 when left out. */
  marginValueCents: optionalCents(),
}).superRefine(bandRangeRefine);
export type FrameMarkupBandCreate = z.infer<typeof FrameMarkupBandCreateSchema>;

/** UC-PRICING-004 A2: a change in place under the band's version; its scope is fixed (a scope's band is retired, then added). */
export const FrameMarkupBandPatchSchema = z.strictObject({
  productId: nullableId(),
  supplierId: nullableId(),
  brand: optionalText(BAND_BRAND_MAX),
  rangeStartCents: optionalCents(),
  rangeEndCents: wholeSchema(centsRule({ required: false, nullable: true })).transform((v) => v as number | null | undefined).optional(),
  marginPercentBp: optionalMarkupBp(),
  marginValueCents: optionalCents(),
}).superRefine(bandRangeRefine);
export type FrameMarkupBandPatch = z.infer<typeof FrameMarkupBandPatchSchema>;

export const FrameMarkupBandViewSchema = FrameMarkupBandRefSchema.extend({
  branch: PricingBranchRefSchema.nullable(),
  product: z.object({ id: z.string(), sku: z.string(), name: z.string() }).nullable(),
  supplier: PricingSupplierRefSchema.nullable(),
  version: z.number().int(),
  createdBy: PricingUserRefSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** A retired band: kept with its history, never resolving again (W15). */
  retiredAt: z.string().nullable(),
  retiredBy: PricingUserRefSchema.nullable(),
});
export type FrameMarkupBandView = z.infer<typeof FrameMarkupBandViewSchema>;
export const FrameMarkupBandPageSchema = z.object({ items: z.array(FrameMarkupBandViewSchema), nextCursor: z.string().optional() });
export const CopyTenantBandsInputSchema = z.strictObject({ branchId: requiredId() });
export type CopyTenantBandsInput = z.infer<typeof CopyTenantBandsInputSchema>;
export const CopyTenantBandsResultSchema = z.object({ branchId: z.string(), bands: z.array(FrameMarkupBandViewSchema) });

// --- Product markups (UC-PRICING-005; BR-PRICING-006, -007) ------------------------------------------------------------------

export const ProductMarkupQuerySchema = z.object({
  productId: queryId,
  /** `org` for the tenant's markups, or a branch id. */
  branchId: scopeParam,
  includeRemoved: queryFlag,
});
export type ProductMarkupQuery = z.infer<typeof ProductMarkupQuerySchema>;

export const ProductMarkupPutSchema = z.strictObject({
  productId: requiredId(),
  /** Null or left out: the tenant's markup; a branch id: that branch's, which wins there. */
  branchId: nullableId(),
  percentBp: requiredMarkupBp(),
  /** Added in VAT-inclusive cents (W2); 0 when left out. */
  valueCents: optionalCents(),
});
export type ProductMarkupPut = z.infer<typeof ProductMarkupPutSchema>;

export const ProductMarkupViewSchema = ProductMarkupRefSchema.extend({
  productId: z.string(),
  product: z.object({ id: z.string(), sku: z.string(), name: z.string() }).nullable(),
  branch: PricingBranchRefSchema.nullable(),
  version: z.number().int(),
  createdBy: PricingUserRefSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  removedAt: z.string().nullable(),
  removedBy: PricingUserRefSchema.nullable(),
});
export type ProductMarkupView = z.infer<typeof ProductMarkupViewSchema>;
export const ProductMarkupListSchema = z.object({ items: z.array(ProductMarkupViewSchema) });

// --- Supplier price lists (UC-PRICING-009; BR-PRICING-019) -------------------------------------------------------------------

/** `?status=Current,Superseded` — one status or several, comma-separated; Current when left out (UC-PRICING-009 A3). */
const statusesParam = z.unknown().superRefine((v, ctx) => {
  const parts = typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
  if (parts.length === 0 || parts.some((p) => !(SUPPLIER_PRICE_LIST_STATUSES as readonly string[]).includes(p))) fail(ctx, "invalid", `one or more of ${SUPPLIER_PRICE_LIST_STATUSES.join(", ")}, comma-separated`);
}).transform((v): SupplierPriceListStatus[] => [...new Set((v as string).split(",").map((s) => s.trim()).filter(Boolean))] as SupplierPriceListStatus[]).default(["Current"]);

export const SupplierPriceListQuerySchema = z.object({
  supplierId: queryId,
  /** `org` for the organisation's lists, a branch id for that branch's own; every scope when left out. */
  branchId: scopeParam,
  status: statusesParam,
  limit: limitParam(200, 50),
  cursor: cursorParam,
});
export type SupplierPriceListQuery = z.infer<typeof SupplierPriceListQuerySchema>;

export const SupplierPriceListViewSchema = z.object({
  id: z.string(),
  supplierId: z.string(),
  supplier: PricingSupplierRefSchema.nullable(),
  scope: z.enum(PRICING_SCOPES),
  branchId: z.string().nullable(),
  branch: PricingBranchRefSchema.nullable(),
  name: z.string(),
  kind: z.enum(SUPPLIER_PRICE_LIST_KINDS),
  /** The supplier's date for the list: its costs are in force from it. */
  listDate: z.string(),
  /** Closed at the next list's date when superseded, or the day a branch's list was ended from. */
  effectiveTo: z.string().nullable(),
  pricesIncludeVat: z.boolean(),
  /** 0–10 000 bp; null: the supplier's default discount. */
  discountBp: z.number().int().nullable(),
  lensMarkupBp: z.number().int().nullable(),
  /** The lens rounding of the list's scope as it resolves now (BR-PRICING-018). */
  lensRoundingRule: z.enum(ROUNDING_RULES),
  lensRoundingSource: z.enum(["branch", "tenant", "default"]),
  status: z.enum(SUPPLIER_PRICE_LIST_STATUSES),
  withdrawnAt: z.string().nullable(),
  lineCount: z.number().int(),
  sourceImportJobId: z.string().nullable(),
  /** Who applied the list and when it became Current (its terms' history entry). */
  appliedBy: PricingUserRefSchema.nullable(),
  appliedAt: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SupplierPriceListView = z.infer<typeof SupplierPriceListViewSchema>;
export const SupplierPriceListPageSchema = z.object({ items: z.array(SupplierPriceListViewSchema), nextCursor: z.string().optional() });

/** UC-PRICING-009 A2: end a branch's own list from a date — today or later. A Current list's terms do not change. */
export const SupplierPriceListPatchSchema = z.strictObject({ effectiveTo: requiredDate() });
export type SupplierPriceListPatch = z.infer<typeof SupplierPriceListPatchSchema>;

// --- The price-list export (UC-PRICING-012; BR-PRICING-027) ------------------------------------------------------------------

/** BR-PRICING-027: the file's columns, CAT-41's defaults first. */
export const PRICE_LIST_EXPORT_HEADER = ["sku", "description", "priceCents", "priceSource", "vatTreatment", "branchCode", "date"] as const;

const categorySelection = z.unknown().superRefine((v, ctx) => {
  if (v === undefined) return;
  const parts = Array.isArray(v) ? v : typeof v === "string" ? [v] : null;
  if (!parts || parts.length === 0 || parts.some((p) => !(PRODUCT_CATEGORIES as readonly unknown[]).includes(p))) fail(ctx, "invalid", `one or more of ${PRODUCT_CATEGORIES.join(", ")}`);
}).transform((v): ProductCategory[] | undefined => (v === undefined ? undefined : [...new Set(Array.isArray(v) ? v : [v])] as ProductCategory[])).optional();

export const PriceListExportRequestSchema = z.strictObject({
  branchId: requiredId(),
  /** The day the prices are for; today when left out (BR-PRICING-014). */
  date: optionalDate(),
  /**
   * The products ticked. More than the cap (PRICE_LIST_EXPORT_ROW_CAP) is the export's own refusal, `range`, `too_many_rows`,
   * with the count (UC-PRICING-012 E1) — not a bound of the list here, which would answer first without it.
   */
  productIds: z.array(z.string().min(1).max(64)).min(1).optional(),
  /** Or a tab and its filters (UC-PRICING-012 A1): the category, the search, the supplier, the brand. */
  category: categorySelection,
  q: z.string().trim().min(2).max(100).optional(),
  supplierId: z.string().min(1).max(64).optional(),
  brand: z.string().trim().min(1).max(60).optional(),
}).superRefine((v, ctx) => {
  if (v.productIds === undefined && v.category === undefined && v.q === undefined) ctx.addIssue({ code: "custom", path: ["productIds"], message: "tick products, or name a tab and its filters", params: { code: "required" } });
  if (v.productIds !== undefined && (v.category !== undefined || v.q !== undefined || v.supplierId !== undefined || v.brand !== undefined)) ctx.addIssue({ code: "custom", path: ["productIds"], message: "ticked products or a tab's filters, not both", params: { code: "ambiguous" } });
});
export type PriceListExportRequest = z.infer<typeof PriceListExportRequestSchema>;

/** The pricing limits, for the PWA's forms (the discount range is the lists'). */
export const PRICING_LIMITS = { markupBpMax: MARKUP_BP_MAX, discountBpMax: DISCOUNT_BP_MAX, netCostCentsMax: UNIT_COST_MAX_CENTS, resolveLinesMax: RESOLVE_LINES_MAX, exportRowCap: PRICE_LIST_EXPORT_ROW_CAP } as const;
